import React from 'react';
import HeroCarousel from '../../components/HeroCarousel';
import { Link } from 'react-router-dom';
import { Globe, Users, Calendar, Share2, ArrowUpRight, Feather, Image as ImageIcon } from 'lucide-react';

// ARCHETYPE 11: The Literary Salon — Newspaper editorial, serif typography, drop-cap
// Unique: Bodoni Moda/Lora serif, 3-column editorial layout, drop-cap first letter,
// section names (Verse, Prose, Scribes, Stage, Folios, Recitations)
const SalonTemplate = ({ association: a, activities, theme: t, onShare }) => {
  const C = {
    bg: t.bg || '#FFF9FB',
    primary: t.primary || '#4A1525',
    secondary: t.accent || '#FCE4EC',
    accent: t.secondary || '#263238',
    text: '#2D2327',
  };

  return (
    <div style={{ backgroundColor: C.bg, color: C.text, fontFamily: "'Lora', serif" }} className="min-h-screen">
      <nav className="fixed top-0 left-0 right-0 z-50 border-b" style={{ backgroundColor: C.bg, borderColor: `${C.primary}15` }} data-testid="association-nav">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img src={a.logo_url || a.image_url} alt="" className="w-9 h-9 rounded-full object-cover border" style={{ borderColor: C.primary }} />
            <span className="text-2xl tracking-wider" style={{ fontFamily: "'Bodoni Moda', serif", color: C.primary, fontWeight: 700 }}>{a.short_name}</span>
          </div>
          <div className="hidden md:flex gap-6 text-xs uppercase tracking-wider" style={{ color: C.accent }}>
            {['Verse','Prose','Scribes','Stage','Folios'].map(s => <a key={s} href={`#${s.toLowerCase()}`} className="hover:underline">{s}</a>)}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onShare} className="p-2" data-testid="share-btn"><Share2 className="w-4 h-4" style={{ color: C.primary }} /></button>
            <Link to="/" className="text-xs px-3 py-1.5 border" style={{ borderColor: `${C.primary}20`, color: C.primary }} data-testid="cca-return-badge">CCA Home</Link>
          </div>
        </div>
      </nav>

      {/* Hero — Editorial masthead with clearly visible banner */}
      <section id="verse" className="pt-14" data-testid="association-hero">
        <div className="relative h-[50vh] overflow-hidden">
          {a.hero_images?.length > 0 ? <div className="absolute inset-0"><HeroCarousel images={a.hero_images} className="w-full h-full" /></div> : null}
          <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 30%, ${C.bg}cc 85%, ${C.bg})` }} />
        </div>
        <div className="max-w-5xl mx-auto px-6 text-center pb-12 pt-8">
          <div className="border-t-2 border-b-2 py-8 mb-6" style={{ borderColor: C.primary }}>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl" style={{ fontFamily: "'Bodoni Moda', serif", color: C.primary, fontWeight: 700 }} data-testid="association-name">{a.short_name}</h1>
            <p className="text-lg italic mt-2 opacity-50">{a.name}</p>
          </div>
          <div className="flex items-center justify-center gap-4 text-xs" style={{ color: `${C.primary}88` }}>
            <span>{a.region} Region</span><span>&bull;</span><span>{a.origin||'Cameroon'}</span>
          </div>
          {a.cultural_highlights && <p className="mt-6 max-w-2xl mx-auto italic text-sm leading-relaxed" style={{ color: `${C.text}77` }} data-testid="cultural-highlights-banner">{a.cultural_highlights}</p>}
        </div>
      </section>

      {/* 3-col editorial — The Verse */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8" style={{ columnRule: `1px solid ${C.primary}10` }}>
          <div className="md:col-span-2">
            <Feather className="w-5 h-5 mb-3" style={{ color: C.primary }} />
            <h2 className="text-2xl mb-4" style={{ fontFamily: "'Bodoni Moda', serif", color: C.primary }}>The Verse</h2>
            <p className="first-letter:text-5xl first-letter:font-bold first-letter:float-left first-letter:mr-2 first-letter:leading-none text-base leading-relaxed" style={{ color: `${C.text}88` }} data-testid="association-description">{a.description}</p>
          </div>
          <div className="space-y-6">
            <div className="p-5 border" style={{ borderColor: `${C.primary}15` }}>
              <h3 className="text-sm uppercase tracking-wider mb-2" style={{ color: C.primary }}>Mission</h3>
              <p className="text-sm italic" style={{ color: `${C.text}66` }} data-testid="association-mission">{a.mission}</p>
            </div>
            <div className="p-5" style={{ backgroundColor: C.secondary }}>
              <h3 className="text-sm uppercase tracking-wider mb-2" style={{ color: C.primary }}>Values</h3>
              <div className="flex flex-wrap gap-1" data-testid="association-values">
                {(a.values||[]).map((v,i) => <span key={i} className="px-2 py-0.5 text-xs border" style={{ borderColor: `${C.primary}20`, color: C.primary }}>{v}</span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cultural Prose */}
      <section id="prose" className="py-16 px-6" style={{ backgroundColor: C.secondary }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl mb-6 text-center" style={{ fontFamily: "'Bodoni Moda', serif", color: C.primary }}>Cultural Prose</h2>
          <p className="text-base leading-relaxed italic text-center" style={{ color: `${C.text}77` }} data-testid="cultural-background">{a.cultural_background||a.cultural_highlights}</p>
        </div>
      </section>

      {/* The Scribes — Leadership */}
      <section id="scribes" className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl mb-8 text-center" style={{ fontFamily: "'Bodoni Moda', serif", color: C.primary }}>The Scribes</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center p-5 border" style={{ borderColor: `${C.primary}15` }}>
              <div className="w-16 h-16 rounded-full mx-auto mb-3 overflow-hidden border" style={{ borderColor: C.primary }}>
                {a.president_photo_url
                  ? <img src={a.president_photo_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: C.secondary }}><span className="text-xl" style={{ color: C.primary }}>{(a.president_name||'?')[0]}</span></div>
                }
              </div>
              <h4 className="font-bold" style={{ fontFamily: "'Bodoni Moda', serif" }}>{a.president_name}</h4>
              <p className="text-xs italic" style={{ color: C.primary }}>Chief Scribe</p>
            </div>
            {(a.leadership||[]).map((l,i) => (
              <div key={l.id||i} className="text-center p-5 border" style={{ borderColor: `${C.primary}08` }}>
                <div className="w-16 h-16 rounded-full mx-auto mb-3 overflow-hidden" style={{ backgroundColor: C.secondary }}>
                  {l.photo_url
                    ? <img src={l.photo_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><span className="text-xl opacity-30">{(l.name||'?')[0]}</span></div>
                  }
                </div>
                <h4 className="font-bold">{l.name}</h4>
                <p className="text-xs opacity-50">{l.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* On Stage — Events */}
      <section id="stage" className="py-16 px-6" style={{ backgroundColor: C.secondary }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl mb-6 text-center" style={{ fontFamily: "'Bodoni Moda', serif", color: C.primary }}>On Stage</h2>
          {activities.length > 0 ? (
            <div className="space-y-4">
              {activities.map(ev => (
                <div key={ev.id} className="border-b pb-3" style={{ borderColor: `${C.primary}10` }} data-testid={`event-${ev.id}`}>
                  <h4 className="font-bold" style={{ fontFamily: "'Bodoni Moda', serif" }}>{ev.title}</h4>
                  <p className="text-sm italic opacity-50 mt-1">{ev.description}</p>
                </div>
              ))}
            </div>
          ) : <p className="text-center italic opacity-40">The stage awaits.</p>}
        </div>
      </section>

      {/* The Folios — Gallery */}
      <section id="folios" className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl mb-8 text-center" style={{ fontFamily: "'Bodoni Moda', serif", color: C.primary }}>The Folios</h2>
          {a.gallery_images?.length > 0 ? (
            <div className="columns-2 md:columns-3 gap-4 space-y-4">
              {a.gallery_images.map((img,i) => (
                <div key={i} className="break-inside-avoid border" style={{ borderColor: `${C.primary}10` }} data-testid={`gallery-image-${i}`}>
                  <img src={img.url} alt="" className="w-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          ) : <p className="text-center italic opacity-40">Folios in preparation.</p>}
        </div>
      </section>

      {/* Recitations — Videos */}
      <section className="py-16 px-6" style={{ backgroundColor: C.secondary }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl mb-6 text-center" style={{ fontFamily: "'Bodoni Moda', serif", color: C.primary }}>Recitations</h2>
          {(a.youtube_videos||[]).length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {a.youtube_videos.map((v,i) => (
                <div key={i} className="border" style={{ borderColor: `${C.primary}10` }} data-testid={`video-${i}`}>
                  <div className="aspect-video"><iframe src={`https://www.youtube.com/embed/${v.video_id}`} title={v.title||''} className="w-full h-full" allowFullScreen /></div>
                </div>
              ))}
            </div>
          ) : <p className="text-center italic opacity-40">Recitations coming soon.</p>}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 text-center border-t" style={{ borderColor: `${C.primary}15`, backgroundColor: C.primary, color: C.secondary }}>
        <img src={a.logo_url || a.image_url} alt="" className="w-12 h-12 rounded-full mx-auto mb-4 object-cover border" style={{ borderColor: C.accent }} />
        <h3 className="text-2xl" style={{ fontFamily: "'Bodoni Moda', serif" }}>{a.short_name}</h3>
        <p className="text-sm opacity-40 mt-2 mb-6">{a.name}</p>
        <div className="flex justify-center gap-6 text-xs opacity-30">
          <Link to="/" className="hover:opacity-100">CCA Montreal <ArrowUpRight className="w-3 h-3 inline" /></Link>
          <Link to="/associations" className="hover:opacity-100">Associations</Link>
        </div>
      </footer>
    </div>
  );
};
export default SalonTemplate;
